import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import DPFeedbackIconTooltip, {
  FEEDBACK_TYPES,
} from "../../../../packages/DPFeedbackIconTooltip";
import WPHeading from "../../../../packages/WPHeading";
import { WPTypography } from "../../../../packages/WPTypography";

import SliderCard from "../../../../components/SliderCard";
import { cipAndShutdownStaticConfig } from "../AdvancedSettings.config";

import {
  LICENSE_PERMISSION_IDS,
  LICENSE_STATUS,
} from "../../../../constants/LicenseManagement.constants";

import IconWithTooltip from "../../../../common/styles/components/headings/IconWithTooltip";
import ToggleSwitch from "../../../../common/styles/components/switches/CustomSwitch";

import {
  hasEligibleLicensePermission,
  hasActiveLicensePermission,
  hasAssignedLicensePermission,
} from "../../../../utils/LicenseUtils";
import { getUsePermeabilityFeedback } from "../../../../utils/usePermeabilityFeedback";

import "./CIPShutdownIdentification.scss";

const CIPShutdownIdentification = ({
  cipDynamicConfig,
  pressure,
  suggestedSettings,
  checkedPressureDropOptions,
  resetKey,
  settings,
  defaultSettings,
  handleChange,
}) => {
  const { t } = useTranslation();
  const userLicenses = useSelector((state) => state.userLicenses || []);

  const getOptionLabel = (optionConfig) => {
    const translated = t(`advanceSettings.${optionConfig.labelKey}`);
    return optionConfig.labelKey === "pressureDropAbsThreshold"
      ? translated.replace("<unit>", pressure)
      : translated;
  };

  const permeabilityTriggerOn = Boolean(
    settings.use_permeability_trigger ??
    defaultSettings.use_permeability_trigger,
  );

  const hasUsePermeabilityLicense = hasEligibleLicensePermission(
    userLicenses,
    LICENSE_PERMISSION_IDS.USE_PERMEABILITY,
  );

  const hasPermeabilityPermissionAnyStatus = hasAssignedLicensePermission(
    userLicenses,
    LICENSE_PERMISSION_IDS.USE_PERMEABILITY,
  );

  const usePermeabilityFeedback = useMemo(() => {
    return getUsePermeabilityFeedback({
      userLicenses,
      licensePermissionId: LICENSE_PERMISSION_IDS.USE_PERMEABILITY,
      licenseStatus: LICENSE_STATUS,
      feedbackTypes: FEEDBACK_TYPES,
    });
  }, [userLicenses]);

  const permeabilityGroupKeys = new Set([
    "use_permeability_trigger",
    "permeability_only",
    "permeability_threshold",
  ]);

  const renderRadioRow = (key, config, disabled = false) => {
    const currentValue = settings[key] ?? defaultSettings[key];
    const hasSuggested = suggestedSettings[key] != null;
    return (
      <div className="flex-between-center radio-toggle-row" key={key}>
        <div className="label-recomended-container">
          <label className="font-14 margin-radio">
            {t(`advanceSettings.${config.key}`)}
          </label>
          {hasSuggested && (
            <span className="scale-label-middle">
              {`${t("advanceSettings.sliderLabelMiddle")}: ${suggestedSettings[key] ? t("ValueStrings.yes") : t("ValueStrings.no")}`}
            </span>
          )}
        </div>
        <ToggleSwitch
          id={`toggle_${key}`}
          name={key}
          checked={Boolean(currentValue)}
          onChange={(checked) =>
            handleChange({ type: "radio", key, value: checked })
          }
          disabled={disabled}
          small
        />
      </div>
    );
  };

  const renderSliderCard = ({
    id,
    label,
    settingsKey,
    configObj,
    extraProps = {},
  }) => {
    const isRecommended = suggestedSettings[settingsKey] != null;
    return (
      <div className="slider-card-container" key={id}>
        <SliderCard
          key={`${id}-${resetKey}`}
          id={id}
          label={label}
          min={configObj.minValue}
          max={configObj.maxValue}
          recommended={isRecommended ? suggestedSettings[settingsKey] : null}
          isInteger={configObj.isInteger}
          maxLength={configObj.maxLength}
          inputValue={settings[settingsKey] ?? defaultSettings[settingsKey]}
          onChange={(val) =>
            handleChange({
              type: "slider",
              key: settingsKey,
              value: val,
              min: configObj.ranges.hard.min,
              max: configObj.ranges.hard.max,
            })
          }
          leftLabel={
            configObj.leftLabelKey
              ? t(`advanceSettings.${configObj.leftLabelKey}`)
              : undefined
          }
          rightLabel={
            configObj.rightLabelKey
              ? t(`advanceSettings.${configObj.rightLabelKey}`)
              : undefined
          }
          {...extraProps}
        />
      </div>
    );
  };

  return (
    <>
      <div className="info-section">
        <WPHeading fontNoto size="small" className="mt-24">
          {t("advanceSettings.cipAndShutdownIdentification")}
        </WPHeading>
        <IconWithTooltip
          label={t("helpInformation.cipAndShutdownIdentificationHelp")}
        />
      </div>
      <div className="cip-shutdown-identification-container">
        <WPTypography variant="tableData" className="pressure-drop-label">
          {t("advanceSettings.pressureDropThreshold")}
        </WPTypography>
        <div className="checkbox-group slider-cards-group">
          {Object.entries(cipDynamicConfig.pressure_drop_threshold.options).map(
            ([optionKey, optionConfig]) => {
              const { settingsKey } = optionConfig;
              const isChecked = checkedPressureDropOptions[optionKey];
              return (
                <div className="slider-card-container" key={optionConfig.key}>
                  <SliderCard
                    key={`${optionConfig.key}-${resetKey}`}
                    id={optionConfig.key}
                    label={getOptionLabel(optionConfig)}
                    min={optionConfig.minValue}
                    max={optionConfig.maxValue}
                    recommended={
                      suggestedSettings[settingsKey] != null
                        ? suggestedSettings[settingsKey]
                        : null
                    }
                    isInteger={optionConfig.isInteger}
                    maxLength={optionConfig.maxLength}
                    inputValue={
                      settings[settingsKey] ?? defaultSettings[settingsKey]
                    }
                    onChange={(val) =>
                      handleChange({
                        type: "slider",
                        key: settingsKey,
                        value: val,
                        min: optionConfig.ranges.hard.min,
                        max: optionConfig.ranges.hard.max,
                      })
                    }
                    showCheckbox
                    checked={isChecked}
                    onCheckChange={() =>
                      handleChange({ type: "checkbox", key: optionKey })
                    }
                    disabled={!isChecked}
                  />
                </div>
              );
            },
          )}
        </div>
      </div>
      <div className="slider-cards-group">
        {Object.keys(cipAndShutdownStaticConfig)
          .filter((key) => !permeabilityGroupKeys.has(key))
          .map((key) => {
            const config = cipAndShutdownStaticConfig[key];
            if (
              config.authorization &&
              !hasActiveLicensePermission(userLicenses, config.authorization)
            )
              return null;
            if (config.type === "radio") return renderRadioRow(key, config);
            return renderSliderCard({
              id: config.key,
              label: t(`advanceSettings.${config.key}`),
              settingsKey: key,
              configObj: config,
            });
          })}

        {hasPermeabilityPermissionAnyStatus && (
          <div className="permeability-group-box cip-shutdown-identification-container">
            <WPTypography
              variant="tableData"
              className="use-permeability-label"
            >
              {t("advanceSettings.usePermeability")}
              {usePermeabilityFeedback.type && (
                <DPFeedbackIconTooltip
                  type={usePermeabilityFeedback.type}
                  message={t(usePermeabilityFeedback.messageKey)}
                />
              )}
            </WPTypography>
            {renderRadioRow(
              "use_permeability_trigger",
              cipAndShutdownStaticConfig.use_permeability_trigger,
              !hasUsePermeabilityLicense,
            )}
            {renderRadioRow(
              "permeability_only",
              cipAndShutdownStaticConfig.permeability_only,
              !permeabilityTriggerOn || !hasUsePermeabilityLicense,
            )}
            {renderSliderCard({
              id: cipAndShutdownStaticConfig.permeability_threshold.key,
              label: t(
                `advanceSettings.${cipAndShutdownStaticConfig.permeability_threshold.key}`,
              ),
              settingsKey: "permeability_threshold",
              configObj: cipAndShutdownStaticConfig.permeability_threshold,
              extraProps:
                !permeabilityTriggerOn || !hasUsePermeabilityLicense
                  ? { disabled: true }
                  : {},
            })}
          </div>
        )}
      </div>
    </>
  );
};

export default CIPShutdownIdentification;
